import { useEffect, useState } from 'react'
import { Modal } from './Modal.tsx'

interface TermsAndPrivacyModalProps {
  onClose: () => void
  initialTab?: 'about' | 'terms' | 'privacy'
}

export function TermsAndPrivacyModal({ onClose, initialTab = 'about' }: TermsAndPrivacyModalProps) {
  const [activeTab, setActiveTab] = useState<'about' | 'terms' | 'privacy'>(initialTab)

  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      icon={
        <div className="flex gap-2 -mb-px">
          <button
            onClick={() => setActiveTab('about')}
            className={`px-4 py-2 font-semibold ${
              activeTab === 'about'
                ? 'text-[#667eea] border-b-2 border-[#667eea]'
                : 'text-[#888] hover:text-[#aaa]'
            }`}
          >
            About
          </button>
          <button
            onClick={() => setActiveTab('terms')}
            className={`px-4 py-2 font-semibold ${
              activeTab === 'terms'
                ? 'text-[#667eea] border-b-2 border-[#667eea]'
                : 'text-[#888] hover:text-[#aaa]'
            }`}
          >
            Terms and Conditions
          </button>
          <button
            onClick={() => setActiveTab('privacy')}
            className={`px-4 py-2 font-semibold ${
              activeTab === 'privacy'
                ? 'text-[#667eea] border-b-2 border-[#667eea]'
                : 'text-[#888] hover:text-[#aaa]'
            }`}
          >
            Privacy Statement
          </button>
        </div>
      }
      width="max-w-2xl w-full"
      className="bg-[#2a2a2a] max-h-[80vh] overflow-hidden flex flex-col"
      contentClassName="!px-8 !pt-4 !pb-8 !flex-1 flex flex-col !space-y-0 min-h-0"
      headerClassName="!mb-0"
      overlayClassName="z-[60]"
      usePortal={true}
    >
      <div className="flex-1 overflow-y-auto pr-2 min-h-0">
        {activeTab === 'about' && (
          <div className="space-y-4 text-sm text-[#ccc]">
            <h3 className="text-lg font-semibold text-white mb-3">About Loopmaster</h3>
            <p>
              Loopmaster is a free platform for creating, sharing, and remixing audio content written in code.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">What is Loopmaster?</h4>
            <p>
              Loopmaster allows you to create music and audio using code. Write scripts to generate patterns, sequences,
              and sounds, then share your creations with the community.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Contact</h4>
            <p>
              For the time being you can use our{' '}
              <a
                href="https://discord.gg/NSWaB9dRYh"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#667eea] hover:text-[#7c8ef5] underline"
              >
                Discord
              </a>
              .
            </p>
            <div className="mt-6">
              <a
                href="https://www.buymeacoffee.com/stagas"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img
                  src="/buymeacoffee.png"
                  alt="Buy Me A Coffee"
                  style={{ width: '236px' }}
                />
              </a>
            </div>
          </div>
        )}

        {activeTab === 'terms' && (
          <div className="space-y-4 text-sm text-[#ccc]">
            <h3 className="text-lg font-semibold text-white mb-3">Terms and Conditions</h3>
            <p>
              By using Loopmaster, you agree to these terms. Loopmaster is a free platform for creating, sharing, and
              remixing audio content written in code.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Your Content</h4>
            <p>
              You own the content you create. By uploading content, you allow Loopmaster to display and share it on the
              platform. We may also use your content as prompts for our AI generator to improve and enhance the service.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">No Liability</h4>
            <p>
              Loopmaster is provided "AS IS" without any warranties. Loopmaster and its operators are not liable for any
              damages, data loss, or issues arising from your use of the service.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Changes</h4>
            <p>
              We may update these terms at any time. Continued use of the service means you accept the updated terms.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Account Termination</h4>
            <p>
              We may suspend or terminate your account at any time for any reason, including violation of these terms.
            </p>
          </div>
        )}

        {activeTab === 'privacy' && (
          <div className="space-y-4 text-sm text-[#ccc]">
            <h3 className="text-lg font-semibold text-white mb-3">Privacy Statement</h3>
            <p>
              This describes how Loopmaster handles your personal information. Loopmaster is a free service.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">What We Collect</h4>
            <p>
              We collect the information you provide: your name, email address, and the content you create or upload.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">How We Use It</h4>
            <p>
              We use your information to provide and improve the service and to communicate with you.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Sharing</h4>
            <p>
              We don't sell or rent your personal information. We may share it only as needed to provide the service or
              as required by law.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Security</h4>
            <p>
              We take measures to protect your information, but no system is 100% secure. Loopmaster is provided "AS IS"
              without warranties regarding data security. You use the service at your own risk.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">No Liability</h4>
            <p>
              Loopmaster is not liable for unauthorized access, data breaches, or security incidents involving your
              personal information.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Your Rights</h4>
            <p>
              You can access, update, or delete your personal information at any time through account settings or by
              contacting us.
            </p>
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Changes</h4>
            <p>
              We may update this Privacy Statement from time to time. Changes will be posted on this page.
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}
